import React from "react";
import { useNavigate } from "react-router-dom";
import { MdAdd, MdDeleteForever, MdNotifications } from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import AdminDataTable, { AdminDataTableElement } from "components/ui/AdminDataTable";
import DataTablePagination from "components/ui/DataTablePagination";
import {
  DATA_TABLE_BODY_ROW,
  DATA_TABLE_HEADER_ROW,
  dataTableBodyCellClass,
  dataTableHeaderCellClass,
} from "lib/dataTableClasses";
import { UI_DANGER_BTN, UI_PAGE_SUBTITLE, UI_PAGE_TITLE, UI_PRIMARY_BTN } from "lib/uiClasses";
import { toastError, toastInfo } from "lib/toast";
import ConfirmDialog from "components/ui/ConfirmDialog";
import { AdminNotification, notificationService } from "services/notificationService";
import CreateNotificationDialog from "./CreateNotificationDialog";

const AUDIENCE_LABEL: Record<string, { ar: string; en: string }> = {
  all_students: { ar: "كل الطلاب", en: "All students" },
  all_instructors: { ar: "كل المحاضرين", en: "All instructors" },
  selected_students: { ar: "طلاب محددون", en: "Selected students" },
  selected_instructors: { ar: "محاضرون محددون", en: "Selected instructors" },
  course_students: { ar: "طلاب كورس", en: "Course students" },
  filtered_students: { ar: "طلاب حسب فلتر", en: "Filtered students" },
  filtered_instructors: { ar: "محاضرون حسب فلتر", en: "Filtered instructors" },
};

const STATUS_STYLE: Record<string, string> = {
  sent: "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300",
  sending: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
  scheduled: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300",
  draft: "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-slate-300",
};

export default function NotificationsPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const navigate = useNavigate();

  const [items, setItems] = React.useState<AdminNotification[]>([]);
  const [meta, setMeta] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [deletingAll, setDeletingAll] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(1);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = React.useState(false);

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await notificationService.list({ page, limit: 15 });
      setItems(Array.isArray(res.data) ? res.data : []);
      setMeta(res.meta ?? null);
    } catch {
      setError(isArabic ? "تعذّر تحميل الإشعارات" : "Could not load notifications");
    } finally {
      setLoading(false);
    }
  }, [page, isArabic]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  const requestDeleteAll = () => {
    const total = meta?.total ?? items.length;
    if (total <= 0) {
      toastInfo(isArabic ? "لا توجد إشعارات للحذف" : "No notifications to delete");
      return;
    }
    setDeleteAllOpen(true);
  };

  const handleDeleteAll = async () => {
    setDeletingAll(true);
    try {
      const res = await notificationService.deleteAll();
      toastInfo(
        isArabic
          ? `تم حذف ${res.deleted} إشعار`
          : `Deleted ${res.deleted} notification(s)`
      );
      setPage(1);
      await reload();
    } catch {
      toastError(isArabic ? "تعذّر حذف الإشعارات" : "Could not delete notifications");
      throw new Error("delete_all_failed");
    } finally {
      setDeletingAll(false);
    }
  };

  const totalPages = meta?.last_page ?? 1;
  const totalItems = meta?.total ?? items.length;
  const deleteAllCount = totalItems;
  const fromItem = meta?.from ?? (items.length ? 1 : 0);
  const toItem = meta?.to ?? items.length;

  return (
    <div className="mt-4 grid gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className={UI_PAGE_TITLE}>
            <span className="inline-flex items-center gap-2">
              <MdNotifications className="h-6 w-6 text-brand-500" />
              {isArabic ? "الإشعارات" : "Notifications"}
            </span>
          </h1>
          <p className={UI_PAGE_SUBTITLE}>
            {isArabic
              ? "إرسال إشعارات للطلاب والمحاضرين وعرض سجل الإرسال."
              : "Send notifications to students and instructors and review history."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={`${UI_DANGER_BTN} inline-flex items-center gap-1`}
            onClick={requestDeleteAll}
            disabled={deletingAll || loading || items.length === 0}
          >
            <MdDeleteForever className="h-5 w-5" />
            {deletingAll
              ? isArabic
                ? "جارٍ الحذف…"
                : "Deleting…"
              : isArabic
                ? "حذف الكل"
                : "Delete all"}
          </button>
          <button
            type="button"
            className={`${UI_PRIMARY_BTN} inline-flex items-center gap-1`}
            onClick={() => setCreateOpen(true)}
          >
            <MdAdd className="h-5 w-5" />
            {isArabic ? "إرسال إشعار" : "Send notification"}
          </button>
        </div>
      </div>

      <AdminDataTable
        isArabic={isArabic}
        loading={loading}
        error={error}
        onRetry={reload}
        empty={!loading && items.length === 0}
        emptyTitle={isArabic ? "لا توجد إشعارات بعد" : "No notifications yet"}
      >
        <AdminDataTableElement isArabic={isArabic} minWidth="820px">
          <thead>
            <tr className={DATA_TABLE_HEADER_ROW}>
              <th className={dataTableHeaderCellClass(isArabic, false)}>
                {isArabic ? "العنوان" : "Title"}
              </th>
              <th className={dataTableHeaderCellClass(isArabic, false)}>
                {isArabic ? "الجمهور" : "Audience"}
              </th>
              <th className={dataTableHeaderCellClass(isArabic, false, { numeric: true })}>
                {isArabic ? "المستلمون" : "Recipients"}
              </th>
              <th className={dataTableHeaderCellClass(isArabic, false, { numeric: true })}>
                {isArabic ? "نجح / فشل" : "Sent / Failed"}
              </th>
              <th className={dataTableHeaderCellClass(isArabic, false, { compact: true })}>
                {isArabic ? "الحالة" : "Status"}
              </th>
              <th className={dataTableHeaderCellClass(isArabic, false)}>
                {isArabic ? "التاريخ" : "Date"}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr
                key={row.id}
                className={`${DATA_TABLE_BODY_ROW} cursor-pointer`}
                onClick={() => navigate(`/admin/notifications/${row.id}`)}
              >
                <td className={dataTableBodyCellClass(isArabic, { truncate: true })}>
                  <span className="font-semibold text-navy-700 dark:text-white">{row.title}</span>
                </td>
                <td className={dataTableBodyCellClass(isArabic)}>
                  {isArabic
                    ? AUDIENCE_LABEL[row.audience_type]?.ar ?? row.audience_type
                    : AUDIENCE_LABEL[row.audience_type]?.en ?? row.audience_type}
                </td>
                <td className={dataTableBodyCellClass(isArabic, { numeric: true })}>
                  {row.recipients_count}
                </td>
                <td className={dataTableBodyCellClass(isArabic, { numeric: true })}>
                  <span className="text-green-600 dark:text-green-400">{row.sent_count}</span>
                  {" / "}
                  <span className="text-red-600 dark:text-red-400">{row.failed_count}</span>
                </td>
                <td className={dataTableBodyCellClass(isArabic, { compact: true })}>
                  <span
                    className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                      STATUS_STYLE[row.status] ?? STATUS_STYLE.draft
                    }`}
                  >
                    {row.status}
                  </span>
                </td>
                <td className={dataTableBodyCellClass(isArabic)}>{row.created_at ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </AdminDataTableElement>
      </AdminDataTable>

      <DataTablePagination
        isArabic={isArabic}
        page={page}
        totalPages={totalPages}
        fromItem={fromItem}
        toItem={toItem}
        totalItems={totalItems}
        onPageChange={setPage}
      />

      <CreateNotificationDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={reload}
      />

      <ConfirmDialog
        open={deleteAllOpen}
        onClose={() => setDeleteAllOpen(false)}
        onConfirm={handleDeleteAll}
        variant="danger"
        title={isArabic ? "حذف كل الإشعارات" : "Delete all notifications"}
        description={
          isArabic
            ? `سيتم حذف ${deleteAllCount} إشعار من السجل وإنبوكس المستخدمين. لا يمكن التراجع عن هذا الإجراء.`
            : `${deleteAllCount} notification(s) will be removed from history and user inboxes. This cannot be undone.`
        }
        confirmLabel={isArabic ? "حذف الكل" : "Delete all"}
      />
    </div>
  );
}
